/**
 * 编辑学生对话框（student-registry-admin-ui:SC-13/SC-14，design D9 整体替换）。
 *
 * 可编辑：姓名/性别/年级/学段/校区/科目/监护人称呼/与孩子关系/共用手机号/标签。
 * 无报名分数字段。只读提示：学号/介绍人/分享来源/关联商品/注册时间不可改。
 * 手机号变更触发事件提示（保存时提示「手机号变更将触发会话撤销」）。
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Select, Space, Tag } from 'antd';
import { message, notification } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import {
  studentsApi,
  type GuardianRelationship,
  type StudentDetail,
  type UpdateStudent,
} from '../../api/students.api';
import { orgCampusApi } from '../../api/campuses.api';
import { sortCampusesByNumericCode } from '../../utils/campus-sort';
import { subjectsApi, type SubjectItem } from '../../api/subjects.api';
import { gradeStageLabel, toSelectOptions } from '../../api/labels';

const RELATIONSHIPS: { value: GuardianRelationship; label: string }[] = [
  { value: 'FATHER', label: '父亲' },
  { value: 'MOTHER', label: '母亲' },
  { value: 'GRANDPA', label: '爷爷' },
  { value: 'GRANDMA', label: '奶奶' },
  { value: 'OTHER', label: '其他' },
];

const MOBILE_RE = /^(\+?86)?1\d{10}$/;

export function StudentEditModal({
  student,
  open,
  onClose,
  onSaved,
}: {
  student: StudentDetail | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form] = Form.useForm<UpdateStudent & { guardianName?: string; guardianRelationship?: GuardianRelationship }>();
  const [loading, setLoading] = useState(false);
  const [campuses, setCampuses] = useState<{ value: string; label: string }[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);

  useEffect(() => {
    if (student && open) {
      form.setFieldsValue({
        name: student.name,
        gender: student.gender,
        gradeStage: student.gradeStage,
        homeCampusId: student.homeCampusId,
        subjectIds: student.subjectIds,
        guardianMobile: student.guardianMobile.includes('****')
          ? ''
          : student.guardianMobile,
        tags: student.tags,
        guardianName: student.guardians?.[0]?.name,
        guardianRelationship: student.guardians?.[0]?.relationship ?? 'FATHER',
        guardians: student.guardians?.length
          ? student.guardians.map((g) => ({ name: g.name, relationship: g.relationship }))
          : [{ name: '', relationship: 'FATHER' as GuardianRelationship }],
      });
      void orgCampusApi.listCampuses({ status: 'ACTIVE', pageSize: 100 }).then((r) =>
        setCampuses(sortCampusesByNumericCode(r.items).map((c) => ({ value: c.id, label: c.name }))),
      );
      void subjectsApi
        .listSubjects({ status: 'ACTIVE', pageSize: 100 })
        .then((r) => setSubjects(r.items))
        .catch(() => setSubjects([]));
    }
  }, [student, open, form]);

  async function handleOk() {
    if (!student) return;
    try {
      const values = await form.validateFields();
      const mobileChanged =
        values.guardianMobile && !student.guardianMobile.includes(values.guardianMobile);
      setLoading(true);
      await studentsApi.updateStudent(student.id, {
        name: values.name,
        gender: values.gender,
        gradeStage: values.gradeStage,
        homeCampusId: values.homeCampusId,
        subjectIds: values.subjectIds,
        tags: values.tags,
        guardianMobile: values.guardianMobile,
        guardians: [
          {
            name: (values as { guardianName?: string }).guardianName ?? student.guardians?.[0]?.name ?? '',
            relationship:
              (values as { guardianRelationship?: GuardianRelationship }).guardianRelationship ??
              'FATHER',
          },
        ],
      });
      message.success('已保存学生档案');
      if (mobileChanged) {
        const noticeKey = `guardian-mobile-changed-${student.id}`;
        notification.warning({
          key: noticeKey,
          message: '手机号变更已生效',
          description: '原手机号的家长登录会话已撤销，家长需用新手机号重新登录（多孩子边界）。',
          duration: 0,
          closable: false,
          actions: (
            <Button
              type="primary"
              size="small"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => notification.destroy(noticeKey)}
            >
              知道了
            </Button>
          ),
        });
      }
      onSaved();
    } catch (err) {
      if (err instanceof Error && !err.message.includes('validation')) {
        message.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <DraggableModal
      title="编辑学生"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="保存"
      confirmLoading={loading}
      destroyOnHidden
      width={640}
    >
      {student && (
        <Space style={{ marginBottom: 12 }}>
          <Tag>学号：{student.studentNo}（不可修改）</Tag>
        </Space>
      )}
      <Form form={form} layout="vertical" data-testid="form-edit-student">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
            <Input style={{ width: 160 }} data-testid="edit-input-name" />
          </Form.Item>
          <Form.Item name="gender" label="性别" rules={[{ required: true }]}>
            <Select
              style={{ width: 100 }}
              options={[{ value: 'MALE', label: '男' }, { value: 'FEMALE', label: '女' }]}
            />
          </Form.Item>
          <Form.Item name="gradeStage" label="年级/学段" rules={[{ required: true }]}>
            <Select style={{ width: 150 }} options={toSelectOptions(gradeStageLabel)} />
          </Form.Item>
          <Form.Item name="homeCampusId" label="归属校区" rules={[{ required: true }]}>
            <Select style={{ width: 150 }} options={campuses} />
          </Form.Item>
          <Form.Item name="subjectIds" label="科目（可选）">
            <Select
              mode="multiple"
              style={{ width: 220 }}
              placeholder="选择意向科目"
              options={subjects.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Form.Item name="guardianName" label="监护人称呼" rules={[{ required: true, message: '请输入监护人称呼' }]}>
            <Input style={{ width: 140 }} data-testid="edit-input-guardian-name" />
          </Form.Item>
          <Form.Item name="guardianRelationship" label="与孩子关系" rules={[{ required: true, message: '请选择关系' }]}>
            <Select style={{ width: 120 }} options={RELATIONSHIPS} />
          </Form.Item>
          <Form.Item
            name="guardianMobile"
            label="共用手机号"
            rules={[{ pattern: MOBILE_RE, message: '请输入正确的手机号' }]}
          >
            <Input style={{ width: 160 }} placeholder="变更将触发会话撤销" data-testid="edit-input-mobile" />
          </Form.Item>
        </div>
        <Form.Item name="tags" label="学生标签（可选）">
          <Select mode="tags" tokenSeparators={[',']} placeholder="输入标签后回车" />
        </Form.Item>
      </Form>
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message="学号、介绍人、分享来源、关联商品、注册时间不可通过本表单修改；手机号变更时间将在保存后由系统自动更新"
      />
    </DraggableModal>
  );
}
